import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Box,
  FormControl, InputLabel, Select, MenuItem, Typography, Grid, Divider,
  FormControlLabel, Checkbox, Autocomplete, Chip, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import { camParts, seramikParts, ppfParts, aracTurleri } from './PreInterviewConstants';

const capitalizeWords = (text) => {
  if (!text) return '';
  return text.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const kategoriyeGoreParçalar = {
  'Cam Filmi': camParts,
  'PPF Kaplama': ppfParts,
  'Seramik Kaplama': seramikParts
};

const ServiceFormModal = ({ isOpen, onClose, onSave, serviceToEdit }) => {
  const [hizmetAdi, setHizmetAdi] = useState('');
  const [fiyat, setFiyat] = useState('');
  const [aciklama, setAciklama] = useState('');
  const [tip, setTip] = useState('');
  const [isPaket, setIsPaket] = useState(false);
  const [paketIcerikleri, setPaketIcerikleri] = useState([]);
  const [parcaIcerikleri, setParcaIcerikleri] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [ozelFiyatlar, setOzelFiyatlar] = useState({});
  const [garantiSuresiYil, setGarantiSuresiYil] = useState('');

  const serviceTypes = ['Paketler', 'Cam Filmi', 'PPF Kaplama', 'Seramik Kaplama', 'Detay Temizlik', 'Yıkama', 'Göçük Düzeltme', 'Diğer'];
  const parcaDestekliKategoriler = ['Cam Filmi', 'PPF Kaplama', 'Seramik Kaplama'];
  const garantiDestekliKategoriler = ['Cam Filmi', 'PPF Kaplama', 'Seramik Kaplama'];
  const isParcaDestekli = parcaDestekliKategoriler.includes(tip);

  useEffect(() => {
    const q = query(collection(db, "services"), orderBy("hizmetAdi", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setAllServices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (isOpen && serviceToEdit) {
      setHizmetAdi(serviceToEdit.hizmetAdi);
      setFiyat(serviceToEdit.fiyat);
      setAciklama(serviceToEdit.aciklama || '');
      setTip(serviceToEdit.tip || '');
      setIsPaket(serviceToEdit.isPaket || false);
      setPaketIcerikleri(serviceToEdit.paketIcerikleri || []);
      setParcaIcerikleri(serviceToEdit.parcaIcerikleri || []);
      setOzelFiyatlar(serviceToEdit.fiyatlar || {});
      setGarantiSuresiYil(serviceToEdit.garantiSuresiYil || '');
    } else {
      setHizmetAdi(''); setFiyat(''); setAciklama(''); setTip('');
      setIsPaket(false); setPaketIcerikleri([]); setParcaIcerikleri([]); setOzelFiyatlar({});
      setGarantiSuresiYil('');
    }
  }, [serviceToEdit, isOpen]);

  useEffect(() => {
    if (!isPaket || !isParcaDestekli) return;
    const parcalar = kategoriyeGoreParçalar[tip] || [];
    if (parcaIcerikleri.length === 0 && parcalar.length > 0) {
      setParcaIcerikleri(parcalar.map(p => ({ parcaAdi: p, urunId: '', urunAdi: '', fiyat: 0 })));
    }
  }, [isPaket, tip, isParcaDestekli, parcaIcerikleri.length]);

  const handleTipChange = (newTip) => {
    setTip(newTip);
    if (!parcaDestekliKategoriler.includes(newTip) && newTip !== 'Paketler') {
      setIsPaket(false);
      setPaketIcerikleri([]);
      setParcaIcerikleri([]);
    }
    if (parcaDestekliKategoriler.includes(newTip) && isPaket) {
      const parcalar = kategoriyeGoreParçalar[newTip] || [];
      setParcaIcerikleri(parcalar.map(p => ({ parcaAdi: p, urunId: '', urunAdi: '', fiyat: 0 })));
    }
  };

  const handlePaketToggle = (checked) => {
    setIsPaket(checked);
    if (checked && isParcaDestekli) {
      const parcalar = kategoriyeGoreParçalar[tip] || [];
      setParcaIcerikleri(parcalar.map(p => ({ parcaAdi: p, urunId: '', urunAdi: '', fiyat: 0 })));
      setPaketIcerikleri([]);
    } else if (checked && tip === 'Paketler') {
      setParcaIcerikleri([]);
    } else {
      setParcaIcerikleri([]);
      setPaketIcerikleri([]);
    }
  };

  const handleParcaUrunChange = (index, urunId) => {
    const updated = [...parcaIcerikleri];
    if (!urunId) {
      updated[index] = { ...updated[index], urunId: '', urunAdi: '', fiyat: 0 };
    } else {
      const service = allServices.find(s => s.id === urunId);
      if (service) {
        updated[index] = {
          ...updated[index],
          urunId: service.id,
          urunAdi: service.hizmetAdi,
          fiyat: service.fiyat || 0
        };
      }
    }
    setParcaIcerikleri(updated);
  };

  const handleParcaFiyatChange = (index, newFiyat) => {
    const updated = [...parcaIcerikleri];
    updated[index] = { ...updated[index], fiyat: Number(newFiyat) };
    setParcaIcerikleri(updated);
  };

  const handleOzelFiyatChange = (type, value) => {
    setOzelFiyatlar(prev => ({ ...prev, [type]: Number(value) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!hizmetAdi || !fiyat || !tip) {
      alert('Hizmet Adı, Standart Fiyat ve Hizmet Tipi alanları zorunludur.');
      return;
    }

    const serviceData = {
      hizmetAdi: capitalizeWords(hizmetAdi),
      fiyat: Number(fiyat),
      fiyatlar: ozelFiyatlar,
      aciklama, tip, isPaket,
      paketIcerikleri: (isPaket && tip === 'Paketler')
        ? paketIcerikleri.map(s => ({ id: s.id, hizmetAdi: s.hizmetAdi }))
        : [],
      parcaIcerikleri: (isPaket && isParcaDestekli)
        ? parcaIcerikleri.filter(p => p.urunId)
        : [],
      ...(garantiDestekliKategoriler.includes(tip) && garantiSuresiYil
        ? { garantiSuresiYil: Number(garantiSuresiYil) }
        : {})
    };
    onSave(serviceData);
    onClose();
  };

  if (!isOpen) return null;
  const isEditing = !!serviceToEdit;
  const kategoriUrunler = allServices.filter(s => s.tip === tip && !s.isPaket && s.id !== serviceToEdit?.id);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditing ? 'Hizmeti Düzenle' : 'Yeni Hizmet Ekle'}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate autoComplete="off" sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth required>
            <InputLabel id="service-type-label">Hizmet Tipi</InputLabel>
            <Select labelId="service-type-label" value={tip} label="Hizmet Tipi" onChange={(e) => handleTipChange(e.target.value)}>
              {serviceTypes.map(type => (<MenuItem key={type} value={type}>{type}</MenuItem>))}
            </Select>
          </FormControl>

          <TextField
            label="Hizmet Adı" value={hizmetAdi}
            onChange={(e) => setHizmetAdi(e.target.value)}
            onBlur={(e) => setHizmetAdi(capitalizeWords(e.target.value))}
            fullWidth required
            helperText="Kaydettiğinizde otomatik olarak düzgün formata çevrilir"
          />
          <TextField label="Açıklama" value={aciklama} onChange={(e) => setAciklama(e.target.value)} multiline rows={2} fullWidth />

          {(tip === 'Paketler' || isParcaDestekli) && (
            <FormControlLabel
              control={<Checkbox checked={isPaket} onChange={(e) => handlePaketToggle(e.target.checked)} />}
              label={isParcaDestekli
                ? `Bu bir ${tip} paketi mi? (Birden fazla parçaya uygulama içeriyor)`
                : "Bu bir paket mi? (Birden fazla hizmet içeriyor)"}
            />
          )}

          {isPaket && tip === 'Paketler' && (
            <Autocomplete
              multiple
              options={allServices.filter(s => s.id !== serviceToEdit?.id && !s.isPaket)}
              getOptionLabel={(option) => `${option.hizmetAdi} (${option.tip})`}
              value={paketIcerikleri}
              onChange={(e, newValue) => setPaketIcerikleri(newValue)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip key={option.id} label={option.hizmetAdi} {...getTagProps({ index })} size="small" />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Paket İçindeki Hizmetler" placeholder="Hizmet seçin..."
                  helperText="Bu paketin içerdiği hizmetleri seçin" />
              )}
            />
          )}

          {isPaket && isParcaDestekli && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Paket İçeriği — {tip} Parçaları
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Her parça için uygulanacak ürünü ve fiyatını belirleyin. Boş bırakılan parçalar pakete dahil edilmez.
              </Typography>
              {parcaIcerikleri.map((parca, index) => (
                <Grid container spacing={1} key={parca.parcaAdi} alignItems="center" sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" fontWeight="bold">{parca.parcaAdi}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Ürün</InputLabel>
                      <Select
                        value={parca.urunId}
                        label="Ürün"
                        onChange={(e) => handleParcaUrunChange(index, e.target.value)}
                      >
                        {kategoriUrunler.map(s => (
                          <MenuItem key={s.id} value={s.id}>{s.hizmetAdi}</MenuItem>
                        ))}
                        <Divider />
                        <MenuItem value=""><em>- Seçimi Kaldır -</em></MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Fiyat" size="small" type="number" fullWidth
                      value={parca.fiyat || ''}
                      onChange={(e) => handleParcaFiyatChange(index, e.target.value)}
                      disabled={!parca.urunId}
                    />
                  </Grid>
                </Grid>
              ))}
            </Box>
          )}

          {garantiDestekliKategoriler.includes(tip) && (
            <TextField
              label="Garanti Süresi (Yıl)"
              type="number"
              value={garantiSuresiYil}
              onChange={(e) => setGarantiSuresiYil(e.target.value)}
              fullWidth
              helperText="Bu hizmet/ürün için geçerli garanti süresi (yıl olarak). Bilgi amaçlıdır; garanti kaydı harici Solar Gard sayfasında oluşturulur."
              inputProps={{ min: 0, step: 1 }}
            />
          )}

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle1" fontWeight="bold">Fiyatlandırma</Typography>

          <TextField
            label="Standart Fiyat (₺)" type="number" value={fiyat}
            onChange={(e) => setFiyat(e.target.value)} fullWidth required
            helperText={isPaket ? "Paket satış fiyatı (alt ürün fiyatlarından bağımsız)" : "Araç tipi seçilmediğinde veya özel fiyat girilmediğinde bu fiyat kullanılır."}
          />

          <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>Araç Cinsine Göre Özel Fiyatlar (Opsiyonel):</Typography>
          <Grid container spacing={2}>
            {aracTurleri.map(vType => (
              <Grid item xs={6} sm={4} key={vType}>
                <TextField
                  label={`${vType} Fiyatı`} type="number" size="small"
                  value={ozelFiyatlar[vType] || ''}
                  onChange={(e) => handleOzelFiyatChange(vType, e.target.value)}
                  fullWidth placeholder={fiyat.toString()}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={onClose}>İptal</Button>
        <Button onClick={handleSubmit} variant="contained">{isEditing ? 'Güncelle' : 'Kaydet'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceFormModal;